<template>
    <div
        :id="id"
        class="repeatable-container-wrapper"
        :class="{ error }"
    >
        <template v-if="children.length">
            <template
                v-for="(item, index) in children"
                :key="index"
            >
                <RepeatableContainerItem
                    :children="item"
                    :item-index="index"
                    :parent-id="id"
                    :reveal-behavior="revealBehavior"
                    :allow-remove="allowAddRemove && children.length > 1"
                    :removing="removeIndexLoading === index"
                    @download-document="emit('download-document', $event)"
                    @remove-item="handleItemRemove(index)"
                />
            </template>
        </template>
        <div
            v-if="allowAddRemove"
            class="repeatable-container-add"
        >
            <button
                type="button"
                class="minor-btn"
                @click="handleItemAdded"
            >
                <LoadingCircle
                    v-if="addLoading"
                    class="minor-btn-loading"
                />
                {{ t('repeatable-container.add') }}
            </button>
        </div>
        <ErrorText
            v-if="computedError"
            :error="computedError"
        />
    </div>
</template>

<script setup lang="ts">
import ErrorText from 'widget-components/components/ErrorText'
import RepeatableContainerItem from './RepeatableContainerItem.vue'
import { useI18n } from 'widget-components/utilities/i18n'
import { type LazyBlockToolsInject, type ExtendedBlock, type RepeatableContainer } from 'widget-components/integrations/CustomPage/types'
import { computed, inject, ref } from 'vue'
import { lazyBlockToolsInjectionKey } from 'widget-components/integrations/CustomPage/injection-keys'
import { isNotNil } from 'ramda'
import LoadingCircle from '../LoadingCircle/LoadingCircle.vue'

defineOptions({ inheritAttrs: false })

const props = defineProps<{
    id: string
    children: ExtendedBlock[][]
    revealBehavior?: RepeatableContainer['revealBehavior']
    error?: string
    allowAddRemove?: boolean
    indexPath: number[]
}>()

const {
    getLazyBlockProp,
    dispatchComponentAction,
} = inject(lazyBlockToolsInjectionKey, {
    requestBlockConfig: () => {},
    getLazyBlockProp: () => {} ,
    dispatchComponentAction: async () => {},
} as LazyBlockToolsInject)

const loading = computed(() => addLoading.value || removeIndexLoading.value > -1)
const addLoading = ref(false)
const removeIndexLoading = ref(-1)

const emit = defineEmits(['download-document', 'items:add', 'items:remove'])

const { t } = useI18n()

const computedError = computed(() => {
    return getLazyBlockProp<string>(props.id, 'error') || props.error
})

const handleItemAdded = async () => {
    if (loading.value) return

    addLoading.value = true
    const newBlock = await dispatchComponentAction('add-section', { repeatableContainerId: props.id, blockId: props.id })

    if (newBlock) {
        emit('items:add', newBlock)
    }

    addLoading.value = false
}

const handleItemRemove = async (index: number) => {
    if (loading.value) return

    removeIndexLoading.value = index
    const response = await dispatchComponentAction('remove-section', { repeatableContainerId: props.id, blockId: props.id, sectionId: index })
    if (isNotNil(response)) {
        emit('items:remove', index)
    }

    removeIndexLoading.value = -1
}

</script>

<style lang="sass" scoped>
.repeatable-container-add
    display: flex
    justify-content: flex-end

@import "./RepeatableContainer.sass"
</style>

